/**
 * Nine-slice frame-profile metadata. Live preview uses these source regions as
 * cropped sprites; bottom corners stay unpainted until Inkscape splits them.
 * Source bounds are cropped-root SVG user units shared by chamfered, sculptured, and timberbutt.
 */
import type { FrameArtworkProfile } from '../types/doorConfiguration';
import type { RectMm } from '../layout/doorLayout';
import { FRAME_PROFILE_ASSETS } from './frameProfileAssets';

export const FRAME_PROFILE_REQUIRED_SLICE_NAMES = [
  'topLeftCorner',
  'topRightCorner',
  'bottomLeftCorner',
  'bottomRightCorner',
  'headRun',
  'leftJambRun',
  'rightJambRun',
] as const;

export const FRAME_PROFILE_DETAIL_SLICE_NAMES = [
  'headDetailRun',
  'leftJambDetailRun',
  'rightJambDetailRun',
] as const;

export const BUTT_HEAD_SLICE_NAMES = [
  'leftHeadEnd',
  'rightHeadEnd',
  'leftHeadOuter',
  'rightHeadOuter',
] as const;

export type FrameProfileRequiredSliceName = (typeof FRAME_PROFILE_REQUIRED_SLICE_NAMES)[number];
export type FrameProfileDetailSliceName = (typeof FRAME_PROFILE_DETAIL_SLICE_NAMES)[number];
export type ButtHeadSliceName = (typeof BUTT_HEAD_SLICE_NAMES)[number];
export type FrameProfileSliceName =
  | FrameProfileRequiredSliceName
  | FrameProfileDetailSliceName
  | ButtHeadSliceName;

export type FrameProfileStretch = 'none' | 'horizontal' | 'vertical';

export interface FrameProfileSliceDefinition {
  name: FrameProfileSliceName;
  sourceBounds: RectMm;
  stretch: FrameProfileStretch;
}

export interface FrameProfileCornerDimensions {
  width: number;
  height: number;
}

export interface FrameProfileNineSliceContract {
  profile: FrameArtworkProfile;
  viewBox: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
  fixedCornerDimensions: Record<
    'topLeftCorner' | 'topRightCorner' | 'bottomLeftCorner' | 'bottomRightCorner',
    FrameProfileCornerDimensions
  >;
  slices: Record<FrameProfileRequiredSliceName, FrameProfileSliceDefinition>;
  detailSlices: Record<FrameProfileDetailSliceName, FrameProfileSliceDefinition>;
}

/**
 * Cropped root-viewBox coordinates shared by both profile files
 * (`viewBox="0 0 621.29999 1241.3"`), after `translate(-139.35,-79.35)`.
 * These numbers are source-SVG user units, not millimetres.
 */
const VIEW_BOX = FRAME_PROFILE_ASSETS.chamfered.viewBox;

const SOURCE_OUTER_LEFT = 0.65;
const SOURCE_OUTER_TOP = 0.65;
const SOURCE_INNER_LEFT = 55.65;
const SOURCE_INNER_TOP = 55.65;
const SOURCE_INNER_RIGHT = 565.65;
const SOURCE_INNER_BOTTOM = 1175.65;
const SOURCE_TOP_CORNER = 55;
const SOURCE_BOTTOM_CORNER_HEIGHT = 65;
const SOURCE_JAMB_RUN_HEIGHT = 1120;
const SOURCE_HEAD_RUN_WIDTH = 510;
const SOURCE_DETAIL_THICKNESS = 17;
const SOURCE_DETAIL_ORIGIN = SOURCE_INNER_LEFT - SOURCE_DETAIL_THICKNESS;
/** Straight-head sample used to square-end the head without pulling the inner opening to the outer edge. */
const SOURCE_HEAD_END_SAMPLE = 12;
/** Outer outline + highlight band in source units. */
const SOURCE_OUTER_BAND = 8;
/** Timberbutt top corners include the authored inner-edge joint strokes. */
const TIMBERBUTT_TOP_CORNER = 58;

const SHARED_SLICES: Record<FrameProfileRequiredSliceName, FrameProfileSliceDefinition> = {
  topLeftCorner: {
    name: 'topLeftCorner',
    sourceBounds: { x: SOURCE_OUTER_LEFT, y: SOURCE_OUTER_TOP, width: SOURCE_TOP_CORNER, height: SOURCE_TOP_CORNER },
    stretch: 'none',
  },
  topRightCorner: {
    name: 'topRightCorner',
    sourceBounds: { x: SOURCE_INNER_RIGHT, y: SOURCE_OUTER_TOP, width: SOURCE_TOP_CORNER, height: SOURCE_TOP_CORNER },
    stretch: 'none',
  },
  bottomLeftCorner: {
    name: 'bottomLeftCorner',
    sourceBounds: { x: SOURCE_OUTER_LEFT, y: SOURCE_INNER_BOTTOM, width: SOURCE_TOP_CORNER, height: SOURCE_BOTTOM_CORNER_HEIGHT },
    stretch: 'none',
  },
  bottomRightCorner: {
    name: 'bottomRightCorner',
    sourceBounds: { x: SOURCE_INNER_RIGHT, y: SOURCE_INNER_BOTTOM, width: SOURCE_TOP_CORNER, height: SOURCE_BOTTOM_CORNER_HEIGHT },
    stretch: 'none',
  },
  headRun: {
    name: 'headRun',
    sourceBounds: { x: SOURCE_INNER_LEFT, y: SOURCE_OUTER_TOP, width: SOURCE_HEAD_RUN_WIDTH, height: SOURCE_TOP_CORNER },
    stretch: 'horizontal',
  },
  leftJambRun: {
    name: 'leftJambRun',
    sourceBounds: { x: SOURCE_OUTER_LEFT, y: SOURCE_INNER_TOP, width: SOURCE_TOP_CORNER, height: SOURCE_JAMB_RUN_HEIGHT },
    stretch: 'vertical',
  },
  rightJambRun: {
    name: 'rightJambRun',
    sourceBounds: { x: SOURCE_INNER_RIGHT, y: SOURCE_INNER_TOP, width: SOURCE_TOP_CORNER, height: SOURCE_JAMB_RUN_HEIGHT },
    stretch: 'vertical',
  },
};

/**
 * Butt-joint head ends. The mitred inner-head run must not be stretched to the
 * outer left/right; these caps continue the straight head and restore the outer face.
 */
export const BUTT_HEAD_SLICES: Record<ButtHeadSliceName, FrameProfileSliceDefinition> = {
  leftHeadEnd: {
    name: 'leftHeadEnd',
    sourceBounds: {
      x: SOURCE_INNER_LEFT,
      y: SOURCE_OUTER_TOP,
      width: SOURCE_HEAD_END_SAMPLE,
      height: SOURCE_TOP_CORNER,
    },
    stretch: 'horizontal',
  },
  rightHeadEnd: {
    name: 'rightHeadEnd',
    sourceBounds: {
      x: SOURCE_INNER_RIGHT - SOURCE_HEAD_END_SAMPLE,
      y: SOURCE_OUTER_TOP,
      width: SOURCE_HEAD_END_SAMPLE,
      height: SOURCE_TOP_CORNER,
    },
    stretch: 'horizontal',
  },
  leftHeadOuter: {
    name: 'leftHeadOuter',
    sourceBounds: {
      x: SOURCE_OUTER_LEFT,
      y: SOURCE_OUTER_TOP,
      width: SOURCE_OUTER_BAND,
      height: SOURCE_TOP_CORNER,
    },
    stretch: 'none',
  },
  rightHeadOuter: {
    name: 'rightHeadOuter',
    sourceBounds: {
      x: SOURCE_INNER_RIGHT + SOURCE_TOP_CORNER - SOURCE_OUTER_BAND,
      y: SOURCE_OUTER_TOP,
      width: SOURCE_OUTER_BAND,
      height: SOURCE_TOP_CORNER,
    },
    stretch: 'none',
  },
};

export const SOURCE_HEAD_OUTER_BAND = SOURCE_OUTER_BAND;
export const SOURCE_HEAD_CORNER = SOURCE_TOP_CORNER;

const SHARED_DETAIL_SLICES: Record<FrameProfileDetailSliceName, FrameProfileSliceDefinition> = {
  headDetailRun: {
    name: 'headDetailRun',
    sourceBounds: {
      x: SOURCE_INNER_LEFT,
      y: SOURCE_DETAIL_ORIGIN,
      width: SOURCE_HEAD_RUN_WIDTH,
      height: SOURCE_DETAIL_THICKNESS,
    },
    stretch: 'horizontal',
  },
  leftJambDetailRun: {
    name: 'leftJambDetailRun',
    sourceBounds: {
      x: SOURCE_DETAIL_ORIGIN,
      y: SOURCE_INNER_TOP,
      width: SOURCE_DETAIL_THICKNESS,
      height: SOURCE_JAMB_RUN_HEIGHT,
    },
    stretch: 'vertical',
  },
  rightJambDetailRun: {
    name: 'rightJambDetailRun',
    sourceBounds: {
      x: SOURCE_INNER_RIGHT,
      y: SOURCE_INNER_TOP,
      width: SOURCE_DETAIL_THICKNESS,
      height: SOURCE_JAMB_RUN_HEIGHT,
    },
    stretch: 'vertical',
  },
};

const TIMBERBUTT_SLICES: Record<FrameProfileRequiredSliceName, FrameProfileSliceDefinition> = {
  ...SHARED_SLICES,
  topLeftCorner: {
    name: 'topLeftCorner',
    sourceBounds: {
      x: SOURCE_OUTER_LEFT,
      y: SOURCE_OUTER_TOP,
      width: TIMBERBUTT_TOP_CORNER,
      height: TIMBERBUTT_TOP_CORNER,
    },
    stretch: 'none',
  },
  topRightCorner: {
    name: 'topRightCorner',
    sourceBounds: {
      x: SOURCE_INNER_RIGHT + SOURCE_TOP_CORNER - TIMBERBUTT_TOP_CORNER,
      y: SOURCE_OUTER_TOP,
      width: TIMBERBUTT_TOP_CORNER,
      height: TIMBERBUTT_TOP_CORNER,
    },
    stretch: 'none',
  },
};

function contractFor(profile: FrameArtworkProfile): FrameProfileNineSliceContract {
  const topCorner = profile === 'timberbutt' ? TIMBERBUTT_TOP_CORNER : SOURCE_TOP_CORNER;
  return {
    profile,
    viewBox: { ...VIEW_BOX },
    fixedCornerDimensions: {
      topLeftCorner: { width: topCorner, height: topCorner },
      topRightCorner: { width: topCorner, height: topCorner },
      bottomLeftCorner: { width: SOURCE_TOP_CORNER, height: SOURCE_BOTTOM_CORNER_HEIGHT },
      bottomRightCorner: { width: SOURCE_TOP_CORNER, height: SOURCE_BOTTOM_CORNER_HEIGHT },
    },
    slices: profile === 'timberbutt' ? TIMBERBUTT_SLICES : SHARED_SLICES,
    detailSlices: SHARED_DETAIL_SLICES,
  };
}

export const FRAME_PROFILE_NINE_SLICE: Record<FrameArtworkProfile, FrameProfileNineSliceContract> = {
  chamfered: contractFor('chamfered'),
  sculptured: contractFor('sculptured'),
  timberbutt: contractFor('timberbutt'),
};

export function getFrameProfileNineSlice(profile: FrameArtworkProfile): FrameProfileNineSliceContract {
  return FRAME_PROFILE_NINE_SLICE[profile];
}

export function isRectWithinViewBox(
  bounds: RectMm,
  viewBox: { x: number; y: number; width: number; height: number }
): boolean {
  const epsilon = 1e-6;
  return (
    bounds.x + epsilon >= viewBox.x &&
    bounds.y + epsilon >= viewBox.y &&
    bounds.x + bounds.width <= viewBox.x + viewBox.width + epsilon &&
    bounds.y + bounds.height <= viewBox.y + viewBox.height + epsilon
  );
}

export function getProfileAssetViewBox(profile: FrameArtworkProfile) {
  return FRAME_PROFILE_ASSETS[profile].viewBox;
}
